import { useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { StatusBadge } from "../components/common";
import { Reveal } from "../components/Reveal";
import { CONTACT, inquiryMailto } from "../data/content";

const CAPABILITIES = [
    { name: "Opening Control", text: "Establish the operating position before the shift begins." },
    { name: "Midshift Reset", text: "Reassess deliberately as floor conditions change." },
    { name: "KPI Check", text: "Keep performance measures connected to the conditions that produced them." },
    { name: "Closing Handoff", text: "Preserve unresolved issues and context for the next shift." },
];

// Temporary public front door for controlshift.titanhavengroup.com, served from
// the THG corporate deployment until the Control Shift app launches and the
// hostname is re-pointed to it. noindex by design — institutional/SEO source of
// truth stays at titanhavengroup.com/portfolio/control-shift. No pricing, no
// checkout (Stripe lives in the Control Shift app itself).
export default function ControlShiftLanding() {
    useEffect(() => {
        document.title = "Control Shift — Store Manager Control Kit | WarRoom Solutions";
        const set = (sel, attr, val, create) => {
            let el = document.querySelector(sel);
            if (!el && create) {
                el = document.createElement(create.tag);
                Object.entries(create.attrs).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            if (el) el.setAttribute(attr, val);
        };
        set('meta[name="description"]', "content",
            "Control Shift — the Store Manager Control Kit from WarRoom Solutions. Real-time operating control for the person running the floor. Commercial launch in progress.");
        set('meta[name="robots"]', "content", "noindex", { tag: "meta", attrs: { name: "robots" } });
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", "https://controlshift.titanhavengroup.com/");
    }, []);

    return (
        <main className="min-h-screen bg-[#0a0a0a] text-white" data-testid="cs-landing">
            <header className="border-b border-thg-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
                    <a
                        href="https://titanhavengroup.com"
                        data-testid="cs-corporate-link"
                        className="flex items-center gap-3 group"
                    >
                        <img src="/assets/thg-shield-sm.png" alt="" className="h-7 w-auto" />
                        <span className="text-xs tracking-[0.25em] uppercase font-bold text-thg-secondary group-hover:text-white transition-colors duration-300">
                            Titan Haven Group LLC
                        </span>
                    </a>
                    <span className="text-xs tracking-[0.25em] uppercase font-bold text-thg-tertiary">WarRoom Solutions</span>
                </div>
            </header>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-16">
                <Reveal>
                    <div className="flex flex-wrap items-center gap-3">
                        <StatusBadge testId="cs-status">Commercial Launch in Progress</StatusBadge>
                        <span className="text-xs tracking-[0.15em] uppercase text-thg-tertiary">
                            WarRoom Solutions · Commercial Software
                        </span>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium text-white">
                        Control Shift
                    </h1>
                    <p className="mt-3 text-sm tracking-[0.2em] uppercase text-thg-tertiary">
                        Store Manager Control Kit · Also: Manager Control
                    </p>
                </Reveal>
                <Reveal delay={0.2}>
                    <p className="mt-8 text-base md:text-lg leading-relaxed text-thg-secondary max-w-2xl">
                        Real-time operating control for the person running the floor. Control Shift gives store
                        managers a structured operating layer for live shifts — what needs attention now, what
                        changed, what was done about it, and what gets handed off — built on the operating
                        discipline WarRoom Solutions publishes.
                    </p>
                </Reveal>
                <Reveal delay={0.3} className="mt-12">
                    <div className="relative overflow-hidden border border-thg-border">
                        <img
                            src="/assets/products/control-shift-screenshot.png"
                            alt="Control Shift interface — shift control view with checkpoints and KPI panel"
                            className="w-full object-cover object-top"
                            loading="eager"
                        />
                        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_70px_rgba(0,0,0,0.5)]" aria-hidden="true" />
                    </div>
                    <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-thg-tertiary">
                        Authentic product capture — Control Shift shift-control view
                    </p>
                </Reveal>
            </section>

            <section className="border-t border-thg-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <Reveal>
                        <h2 className="font-display text-2xl md:text-3xl font-medium text-white">
                            Structured around the operating day
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 border-t border-l border-thg-border">
                        {CAPABILITIES.map((c, i) => (
                            <Reveal key={c.name} delay={i * 0.06} className="border-b border-r border-thg-border p-8">
                                <p className="text-xs tracking-[0.2em] font-bold text-thg-red uppercase">{c.name}</p>
                                <p className="mt-3 text-sm leading-relaxed text-thg-secondary">{c.text}</p>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <p className="mt-8 text-sm leading-relaxed text-thg-secondary max-w-2xl">
                            StoreCentral is the separate, broader WarRoom Solutions retail operating platform,
                            with its own native Shift Command workflow and manager-control capabilities. Control
                            Shift is the standalone live-shift application — neither product embeds or depends on
                            the other.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2} className="mt-10">
                        <div className="relative overflow-hidden border border-thg-border">
                            <img
                                src="/assets/products/control-shift-context.png"
                                alt="Control Shift in operating context — manager control layer"
                                className="w-full object-cover object-top"
                                loading="lazy"
                            />
                            <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_70px_rgba(0,0,0,0.5)]" aria-hidden="true" />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-thg-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <Reveal className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                        <div className="max-w-xl">
                            <h2 className="font-display text-2xl md:text-3xl font-medium text-white">
                                Access is being finalized.
                            </h2>
                            <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary" data-testid="cs-access-note">
                                Commercial launch is in progress and self-serve access is being finalized.
                                Retail operators interested in the limited pilot program can reach WarRoom
                                Solutions directly.
                            </p>
                        </div>
                        <a
                            href={inquiryMailto("Pilot / Product Inquiry — Control Shift")}
                            data-testid="cs-inquiry-cta"
                            className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-6 py-3 text-sm tracking-wide uppercase shrink-0"
                        >
                            Contact WarRoom Solutions <ArrowUpRight size={16} />
                        </a>
                    </Reveal>
                </div>
            </section>

            <footer className="border-t border-thg-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <p className="text-xs text-thg-tertiary">
                        A WarRoom Solutions product · Titan Haven Group LLC
                    </p>
                    <a
                        href={`mailto:${CONTACT.email}`}
                        data-testid="cs-contact-email"
                        className="text-xs text-thg-secondary hover:text-white transition-colors duration-300"
                    >
                        {CONTACT.email}
                    </a>
                </div>
            </footer>
        </main>
    );
}
